import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { earningsApi, type Earnings, type EarningsDay, type EarningsStage } from "@/lib/api/earnings";
import { ApiRequestError } from "@/lib/api/client";
import { qk } from "@/lib/query-client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorState } from "@/components/ui/EmptyState";
import { Icon } from "@/components/layout/Icon";
import { cn } from "@/lib/utils/cn";
import { formatPunchTime, formatWorkedMinutes } from "@/lib/utils/attendance-date";
import { EarningsTimeline } from "./EarningsTimeline";
import { useNow } from "./useNow";
import { LIVE_REFRESH_MS, LIVE_TICK_MS, accruedToday, accruingEarned, liveNextLine, liveNowLine, liveProgress, liveStatusLabel, minutesLabel, notCheckedInToday } from "./liveCopy";
import { useAmountPrivacy } from "./useAmountPrivacy";

/** My Earnings - what the employee has earned so far this month.
 *
 * Every figure comes from GET /employee/self/earnings, which runs the payroll
 * engine itself on the settled attendance; nothing is calculated here. The
 * total only moves when the server says a day was settled (a check-out, an
 * approved correction) - there is no ticking counter. */
const HISTORY_MONTHS = 12;

function shiftPeriod(period: string, delta: number): string {
  const [y, m] = period.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function MyEarningsCard() {
  // null = the current month; otherwise a past month the employee stepped back to.
  const [viewed, setViewed] = useState<string | null>(null);
  const currentPeriod = useRef<string | null>(null);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: qk.earnings.mine(viewed ?? "current"),
    queryFn: () => earningsApi.mine(viewed ?? undefined),
    // Salary data: never keep a stale copy around after the tab is closed.
    gcTime: 5 * 60 * 1000,
    placeholderData: (previous) => previous,
    // While checked in, ask again each minute so today's pay follows the time
    // worked. The server works every figure out; nothing is ticked here.
    refetchInterval: (query) => (query.state.data?.live?.active ? LIVE_REFRESH_MS : false),
  });
  if (viewed === null && data?.period) currentPeriod.current = data.period;

  if (isLoading) return <EarningsSkeleton />;
  if (error) {
    // No access to salary self-service: the card is simply not shown.
    if (error instanceof ApiRequestError && (error.status === 403 || error.status === 404)) return null;
    return (
      <Card>
        <ErrorState title="Couldn't load your earnings" onRetry={() => refetch()} className="py-10" />
      </Card>
    );
  }
  if (!data || !data.enabled) return null;

  const current = currentPeriod.current ?? data.period;
  const shown = data.period;
  const nav: MonthNav = {
    canPrev: shown > shiftPeriod(current, -HISTORY_MONTHS),
    canNext: shown < current,
    onPrev: () => setViewed(shiftPeriod(shown, -1)),
    onNext: () => {
      const next = shiftPeriod(shown, 1);
      setViewed(next >= current ? null : next);
    },
    busy: isFetching,
  };

  if (!data.calculated) {
    return (
      <Card data-testid="earnings-card">
        <CardHeader>
          <div>
            <CardTitle>My Earnings</CardTitle>
            <MonthSwitcher label={data.period_label} nav={nav} />
          </div>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="earnings-unavailable">
            {data.reason || "Your earnings for this month cannot be estimated yet."}
          </p>
        </CardBody>
      </Card>
    );
  }
  return <EarningsView e={data} nav={nav} />;
}

interface MonthNav {
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  busy: boolean;
}

function MonthSwitcher({ label, nav }: { label: string; nav?: MonthNav }) {
  if (!nav) return <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>;
  return (
    <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
      <button
        type="button"
        onClick={nav.onPrev}
        disabled={!nav.canPrev || nav.busy}
        aria-label="Previous month"
        className="rounded p-0.5 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"
      >
        <Icon name="chevron-left" className="h-4 w-4" />
      </button>
      <span className="min-w-[96px] text-center" aria-live="polite" data-testid="earnings-month">
        {label}
      </span>
      <button
        type="button"
        onClick={nav.onNext}
        disabled={!nav.canNext || nav.busy}
        aria-label="Next month"
        className="rounded p-0.5 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"
      >
        <Icon name="chevron-right" className="h-4 w-4" />
      </button>
    </div>
  );
}

export function EarningsView({ e, nav }: { e: Earnings; nav?: MonthNav }) {
  const [showDays, setShowDays] = useState(false);
  const { money, masked, toggle } = useAmountPrivacy();
  const todayCounted = e.today_state === "confirmed" && e.today_earnings > 0;
  const live = e.live?.active ? e.live : undefined;
  const now = useNow(!!live, LIVE_TICK_MS);
  const heroAmount = live ? accruingEarned(live, e.earned_accruing, now) : e.earned_to_date;

  return (
    <Card data-testid="earnings-card" className="overflow-hidden dark:border-slate-700 dark:bg-slate-900">
      <CardHeader className="flex-wrap gap-2">
        <div>
          <CardTitle className="dark:text-slate-100">My Earnings</CardTitle>
          <MonthSwitcher label={e.period_label} nav={nav} />
        </div>
        <div className="flex items-center gap-2">
          <StageChip stage={e.stage} />
          <button
            type="button"
            onClick={toggle}
            aria-pressed={masked}
            aria-label={masked ? "Show amounts" : "Hide amounts"}
            title={masked ? "Show amounts" : "Hide amounts"}
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            data-testid="earnings-mask"
          >
            <Icon name={masked ? "eye-off" : "eye"} className="h-4 w-4" />
          </button>
        </div>
      </CardHeader>

      <CardBody className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400">Estimated earnings this month</p>
            <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums sm:text-4xl dark:text-white">
              <CountUp value={heroAmount} />
            </p>
            {live ? (
              <LiveToday live={live} money={money} settled={e.earned_to_date} now={now} />
            ) : todayCounted ? (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300" data-testid="earnings-today-gain">
                <Icon name="trending-up" className="h-3.5 w-3.5" />
                {money(e.today_earnings)} added today
              </p>
            ) : e.today_state === "pending" ? (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                <Icon name="clock" className="h-3.5 w-3.5" />
                {notCheckedInToday(e.days, e.as_of)
                  ? `You haven't checked in today. Check in to start earning today's ${money(e.today_earnings)}.`
                  : `Today's ${money(e.today_earnings)} counts once your attendance is confirmed`}
              </p>
            ) : null}
            <p className="mt-3 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              An estimate of gross pay before PF, ESI and tax, from attendance confirmed through {formatShortDate(e.as_of)}. Your payslip is the final amount.
            </p>
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3">
            <Stat label="Today's estimate" value={money(e.today_earnings)} muted={!todayCounted && !live} />
            <Stat label={e.salary_type === "monthly" ? "Monthly salary" : "Month at full attendance"} value={money(e.monthly_salary)} />
            <Stat label="Per-day salary" value={money(e.per_day_salary)} />
            <Stat label="Present days" value={formatDays(e.present_days)} />
            <Stat label="Working days left" value={String(e.remaining_working_days)} />
            <Stat label="Est. month-end" value={money(e.estimated_month_end)} />
          </dl>
          {(e.remaining_working_days > 0 || e.pending_days > 0) && (
            <p className="text-xs text-slate-500 dark:text-slate-400 md:col-span-2" data-testid="earnings-projection-note">
              Month-end assumes you attend every remaining working day and that days waiting for attendance are confirmed.
            </p>
          )}
        </div>

        <MonthProgress e={e} />

        <EarningsTimeline days={e.days} monthLabel={e.period_label} />

        {e.extras.length > 0 && (
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Approved extras this month</p>
            <ul className="mt-2 flex flex-wrap gap-2">
              {e.extras.map((x) => (
                <li key={x.code + x.name} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm dark:border-slate-700">
                  <span className="text-slate-500 dark:text-slate-400">{x.name}</span>{" "}
                  <span className="font-medium text-slate-800 tabular-nums dark:text-slate-100">{money(x.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <StageTrack stage={e.stage} />

        {e.payroll?.net_pay != null && e.payroll.net_pay > 0 && (
          <div className="rounded-lg border border-primary-200 bg-primary-50/60 px-4 py-3 dark:border-primary-800 dark:bg-primary-950/40" data-testid="earnings-payroll">
            <p className="text-sm font-medium text-primary-900 dark:text-primary-100">
              {e.payroll.finalized ? "Finalized payslip" : "Approved payroll"}
            </p>
            <p className="mt-0.5 text-sm text-primary-900/80 dark:text-primary-200">
              Gross {money(e.payroll.gross_earnings)} · Net pay <span className="font-semibold">{money(e.payroll.net_pay)}</span>
            </p>
          </div>
        )}

        {e.notes && e.notes.length > 0 && (
          <ul className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
            {e.notes.map((n) => (
              <li key={n} className="flex gap-1.5">
                <Icon name="info" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {n}
              </li>
            ))}
          </ul>
        )}

        {e.days.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setShowDays((v) => !v)}
              aria-expanded={showDays}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline dark:text-primary-300"
            >
              <Icon name={showDays ? "chevron-down" : "chevron-right"} className="h-4 w-4" />
              {showDays ? "Hide" : "View"} date-wise breakdown
            </button>
            {showDays && <DayBreakdown days={e.days} />}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** Today so far, while checked in. All figures are the server's; the amount
 * steps up at the half-day and full-day marks because that is how payroll pays. */
function LiveToday({ live, money, settled, now }: { live: NonNullable<Earnings["live"]>; money: (n: number) => string; settled: number; now: number }) {
  const pct = liveProgress(live);
  return (
    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2.5 dark:border-emerald-800 dark:bg-emerald-950/40" data-testid="earnings-live">
      <p className="flex items-center justify-between gap-2 text-xs font-medium text-emerald-900 dark:text-emerald-200">
        <span>Working now · {minutesLabel(live.worked_minutes)} worked</span>
        <span className="font-normal text-emerald-800/80 dark:text-emerald-300/80">as of {new Date(live.as_of).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}</span>
      </p>
      <p className="mt-1 text-sm font-semibold text-slate-900 tabular-nums dark:text-white" data-testid="earnings-live-accrued">
        Earning today by time: {money(accruedToday(live, now))}
      </p>
      <p className="mt-0.5 text-xs text-slate-700 dark:text-slate-200" data-testid="earnings-live-now">
        Payroll pays {liveStatusLabel(live.status).toLowerCase()}: {liveNowLine(live, money)}
      </p>
      <div
        className="mt-2 h-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900"
        role="progressbar"
        aria-label="Share of a full day worked"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
      >
        <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700 motion-reduce:transition-none" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1.5 text-xs text-emerald-900/90 dark:text-emerald-200" data-testid="earnings-live-next">
        {liveNextLine(live, money)}
      </p>
      <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
        Rises with the time you work, on top of {money(settled)} already confirmed. Payroll confirms the day when you check out and pays it as a half or full day, so what is confirmed at check-out can be lower than the live figure in between.
      </p>
    </div>
  );
}

function Stat({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className={cn("mt-0.5 truncate text-sm font-semibold tabular-nums", muted ? "text-slate-400 dark:text-slate-500" : "text-slate-800 dark:text-slate-100")}>
        {value}
      </dd>
    </div>
  );
}

/** Working-day progress with the 25/50/75/100% milestones, and earnings
 * against the month-end estimate. */
function MonthProgress({ e }: { e: Earnings }) {
  const dayPct = clampPct(e.progress_percent);
  const earnPct = e.estimated_month_end > 0 ? clampPct(((e.live?.active ? e.earned_accruing : e.earned_to_date) / e.estimated_month_end) * 100) : 0;
  return (
    <div className="space-y-4">
      {e.message && (
        <p className="flex items-start gap-2 text-sm font-medium text-emerald-800 dark:text-emerald-300" data-testid="earnings-message" role="status">
          <Icon name="target" className="mt-0.5 h-4 w-4 shrink-0" />
          {e.message}
        </p>
      )}
      <div>
        <div className="flex items-baseline justify-between text-xs text-slate-500 dark:text-slate-400">
          <span>Working days</span>
          <span className="tabular-nums">
            {formatDays(e.completed_working_days)} of {e.planned_working_days}
          </span>
        </div>
        <div
          className="relative mt-1.5 h-2.5 rounded-full bg-slate-100 dark:bg-slate-800"
          role="progressbar"
          aria-label="Working days completed"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(dayPct)}
        >
          <div className="h-full rounded-full bg-primary-600 transition-[width] duration-700 ease-out motion-reduce:transition-none" style={{ width: `${dayPct}%` }} />
          {e.milestones.map((m) => (
            <span
              key={m.percent}
              title={`${m.percent}%: ${m.message}`}
              className={cn(
                "absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2",
                m.reached ? "border-white bg-emerald-500 dark:border-slate-900" : "border-white bg-slate-300 dark:border-slate-900 dark:bg-slate-600",
              )}
              style={{ left: `${Math.min(m.percent, 98.5)}%` }}
            />
          ))}
        </div>
        <div className="mt-1 flex justify-between px-0.5 text-[11px] text-slate-400 tabular-nums">
          {e.milestones.map((m) => (
            <span key={m.percent} className={m.reached ? "font-medium text-emerald-700 dark:text-emerald-400" : undefined}>
              {m.percent}%
            </span>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-baseline justify-between text-xs text-slate-500 dark:text-slate-400">
          <span>Earned against month-end estimate</span>
          <span className="tabular-nums">{Math.round(earnPct)}%</span>
        </div>
        <div className="mt-1.5 h-2 rounded-full bg-slate-100 dark:bg-slate-800" role="progressbar" aria-label="Earnings against estimate" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(earnPct)}>
          <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700 ease-out motion-reduce:transition-none" style={{ width: `${earnPct}%` }} />
        </div>
      </div>
    </div>
  );
}

const STAGES: { key: EarningsStage; label: string }[] = [
  { key: "pending_attendance", label: "Pending attendance" },
  { key: "attendance_confirmed", label: "Attendance confirmed" },
  { key: "estimate_updated", label: "Estimate updated" },
  { key: "payroll_processing", label: "Payroll processing" },
  { key: "payroll_approved", label: "Payroll approved" },
  { key: "payroll_paid", label: "Paid" },
];

function stageLabel(stage: EarningsStage) {
  return STAGES.find((s) => s.key === stage)?.label ?? stage;
}

function StageChip({ stage }: { stage: EarningsStage }) {
  const tone =
    stage === "payroll_paid"
      ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
      : stage === "pending_attendance"
        ? "bg-amber-50 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
        : "bg-primary-50 text-primary-800 dark:bg-primary-950/60 dark:text-primary-200";
  return (
    <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", tone)} data-testid="earnings-stage">
      {stageLabel(stage)}
    </span>
  );
}

/** The six steps of a month's pay; the current one is highlighted. */
function StageTrack({ stage }: { stage: EarningsStage }) {
  const current = STAGES.findIndex((s) => s.key === stage);
  return (
    <ol className="flex gap-1 overflow-x-auto pb-1" aria-label="Pay progress">
      {STAGES.map((s, i) => (
        <li key={s.key} className="min-w-[88px] flex-1" aria-current={i === current ? "step" : undefined}>
          <div className={cn("h-1 rounded-full", i <= current ? "bg-primary-600" : "bg-slate-200 dark:bg-slate-700")} />
          <p
            className={cn(
              "mt-1.5 text-[11px] leading-tight",
              i === current ? "font-semibold text-primary-800 dark:text-primary-200" : i < current ? "text-slate-600 dark:text-slate-300" : "text-slate-400 dark:text-slate-500",
            )}
          >
            {s.label}
          </p>
        </li>
      ))}
    </ol>
  );
}

const STATE_LABEL: Record<EarningsDay["state"], string> = { confirmed: "Confirmed", pending: "Pending", upcoming: "Upcoming" };

function dayStatus(d: EarningsDay) {
  if (d.attendance_status) return d.attendance_status.replace(/_/g, " ");
  if (d.day_type === "weekly_off") return "weekly off";
  if (d.day_type === "holiday") return "holiday";
  return "-";
}

/** The date-wise table on wide screens, a compact list on phones. */
function DayBreakdown({ days }: { days: EarningsDay[] }) {
  const { money } = useAmountPrivacy();
  const [showUpcoming, setShowUpcoming] = useState(false);
  const visible = showUpcoming ? days : days.filter((d) => d.state !== "upcoming");
  const upcoming = days.length - days.filter((d) => d.state !== "upcoming").length;
  return (
    <div className="mt-3" data-testid="earnings-days">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
              <th className="py-2 pr-3 font-medium">Date</th>
              <th className="py-2 pr-3 font-medium">Status</th>
              <th className="py-2 pr-3 font-medium">In / Out</th>
              <th className="py-2 pr-3 font-medium">Worked</th>
              <th className="py-2 pr-3 text-right font-medium">Base</th>
              <th className="py-2 pr-3 text-right font-medium">Overtime</th>
              <th className="py-2 pr-3 text-right font-medium">LOP</th>
              <th className="py-2 text-right font-medium">Day total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {visible.map((d) => (
              <tr key={d.date} className={cn(d.state === "confirmed" ? "text-slate-700 dark:text-slate-200" : "text-slate-400 dark:text-slate-500")}>
                <td className="py-2 pr-3 whitespace-nowrap">{formatShortDate(d.date)}</td>
                <td className="py-2 pr-3">
                  <span className="capitalize">{dayStatus(d)}</span>
                  {d.state !== "confirmed" && <span className="ml-1.5 text-xs">({STATE_LABEL[d.state]})</span>}
                  {d.note && <p className="text-xs text-amber-700 dark:text-amber-400">{d.note}</p>}
                </td>
                <td className="py-2 pr-3 whitespace-nowrap tabular-nums">
                  {d.first_in || d.last_out ? `${formatPunchTime(d.first_in)} / ${formatPunchTime(d.last_out)}` : "-"}
                </td>
                <td className="py-2 pr-3 tabular-nums">{d.worked_minutes > 0 ? formatWorkedMinutes(d.worked_minutes) : "-"}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{money(d.base_earnings)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{d.overtime ? money(d.overtime) : "-"}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{d.lop_adjustment ? money(d.lop_adjustment) : "-"}</td>
                <td className={cn("py-2 text-right font-medium tabular-nums", d.counted && "text-slate-800 dark:text-slate-100")}>{money(d.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="divide-y divide-slate-100 md:hidden dark:divide-slate-800">
        {visible.map((d) => (
          <li key={d.date} className={cn("flex items-start justify-between gap-3 py-2 text-sm", d.state === "confirmed" ? "text-slate-700 dark:text-slate-200" : "text-slate-400 dark:text-slate-500")}>
            <div className="min-w-0">
              <p className="font-medium text-slate-800 dark:text-slate-100">{formatShortDate(d.date)}</p>
              <p className="text-xs capitalize">
                {dayStatus(d)}
                {d.first_in || d.last_out ? ` · ${formatPunchTime(d.first_in)} - ${formatPunchTime(d.last_out)}` : ""}
              </p>
              {d.note && <p className="text-xs text-amber-700 dark:text-amber-400">{d.note}</p>}
            </div>
            <div className="shrink-0 text-right tabular-nums">
              <p className={cn("font-medium", d.counted && "text-slate-800 dark:text-slate-100")}>{money(d.total)}</p>
              {d.lop_adjustment !== 0 && <p className="text-xs">LOP {money(d.lop_adjustment)}</p>}
              {d.overtime > 0 && <p className="text-xs">OT {money(d.overtime)}</p>}
              {!d.counted && <p className="text-xs">{STATE_LABEL[d.state]}</p>}
            </div>
          </li>
        ))}
      </ul>
      {upcoming > 0 && (
        <button type="button" onClick={() => setShowUpcoming((v) => !v)} className="mt-2 text-xs font-medium text-primary-700 hover:underline dark:text-primary-300">
          {showUpcoming ? "Hide upcoming days" : `Show ${upcoming} upcoming days`}
        </button>
      )}
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        Greyed days are not counted yet. Amounts are estimates; approved payroll is shown separately once it is ready.
      </p>
    </div>
  );
}

function EarningsSkeleton() {
  return (
    <Card data-testid="earnings-loading" aria-busy="true">
      <CardHeader>
        <Skeleton className="h-5 w-32" />
      </CardHeader>
      <CardBody className="space-y-5">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-10 w-40" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-9" />
            ))}
          </div>
        </div>
        <Skeleton className="h-2.5 w-full" />
      </CardBody>
    </Card>
  );
}

/** Counts up (or down) to a new value when it changes, once - never a
 * running ticker. Writes to the DOM node directly so the animation does not
 * re-render React; screen readers get the final value only. */
export function CountUp({ value, durationMs = 700 }: { value: number; durationMs?: number }) {
  const { money, masked } = useAmountPrivacy();
  const ref = useRef<HTMLSpanElement>(null);
  const shown = useRef(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const from = shown.current;
    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (masked || reduce || from === value || typeof requestAnimationFrame === "undefined") {
      shown.current = value;
      node.textContent = money(value);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = from + (value - from) * eased;
      shown.current = current;
      node.textContent = money(Math.round(current * 100) / 100);
      if (t < 1) frame = requestAnimationFrame(step);
      else shown.current = value;
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, durationMs, masked, money]);

  return (
    <>
      <span ref={ref} aria-hidden="true" data-testid="earnings-total">
        {money(value)}
      </span>
      <span className="sr-only">{money(value)}</span>
    </>
  );
}

function clampPct(v: number) {
  return Math.max(0, Math.min(100, Number.isFinite(v) ? v : 0));
}

function formatDays(n: number) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function formatShortDate(iso: string) {
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}
